import { getFlagCookie, setFlagCookie } from '@utilities/cookies';
import { useState, useEffect } from 'react';
import { QueryFunctionContext, useQuery } from 'react-query';
import { evaluateFlags } from '@simplywallst/services';
import { v4 as uuid } from 'uuid';
import { FEATURE_KEYS } from '@/constants/env';
import useUser from '../useUser';
import { useMobileDetect } from '../useMobileDetect';
import { useSelector } from 'react-redux';
import { getRegisteredOnCompanyPage } from '@/redux/ui/selectors';

const defaultEntityId = uuid();
export const QUERY_KEY = 'FLAGS';

export function useFlags(flagKey?: string) {
  const {
    data: user,
    isFetched: userIsFetched,
    mutate: userMutate,
  } = useUser();
  const registeredOnCompanyPage = useSelector(getRegisteredOnCompanyPage);
  const isMobile = useMobileDetect();

  const [entityId, setEntityId] = useState(user.flagrEntityId || '');
  const { data, status } = useQuery(
    [QUERY_KEY, entityId, user.registrationDate],
    async (context: QueryFunctionContext<[string, string, number]>) => {
      const { 1: entityId } = context.queryKey;
      const entityContext = {
        isNative: RUNTIME_ENV === 'native',
        isSWSUser: user.emailAddress.endsWith('@simplywallst.com'),
        userId: user.id,
        userBillingStatus: user.billing.status,
        registerDate: user.registrationDate,
        isLoggedIn: user.loggedIn,
        isMobile: isMobile,
        registeredOnCompanyPage: registeredOnCompanyPage,
        // Avoid! The following properties are PII, and should not be used
        email: user.emailAddress,
        countryISO: user.countryISO,
      };
      if (
        typeof window !== 'undefined' &&
        window.location.search.includes('debug')
      ) {
        entityContext['development'] = 1;
      }
      const response = await evaluateFlags({
        entityId,
        entityContext,
        keys: FEATURE_KEYS.split(','),
      });
      return {
        entityId,
        response: response.data.evaluationResults.map(
          ({ flagKey, variantKey, flagID, variantID, variantAttachment }) => ({
            flagKey,
            variantKey,
            flagID,
            variantID,
            variantAttachment,
          })
        ),
      };
    },
    {
      enabled: Boolean(entityId),
      staleTime: Infinity,
    }
  );

  useEffect(() => {
    if (!userIsFetched) return;

    // FIXME - this mutation runs 50+ times because of app re-render issues,
    // need to limit calls, or never save (I wonder if we persist to api)
    function saveEntityIdToUser(entityId: string) {
      userMutate({
        settings: {
          flagr_entityId: entityId,
        },
      });
    }

    async function getEntityId() {
      const flagId = await getFlagCookie();
      if (user.loggedIn) {
        //Has saved entityId
        if (typeof user.flagrEntityId === 'string') {
          // update user's cookie to their profile flagrEntityId
          setFlagCookie(user.flagrEntityId);
          return user.flagrEntityId;
        }

        //Does not have saved entityId
        if (typeof flagId === 'string') {
          saveEntityIdToUser(flagId);
          return flagId;
        } else {
          setFlagCookie(defaultEntityId);
          saveEntityIdToUser(defaultEntityId);
          return defaultEntityId;
        }
      }

      //For logged out / anonymous users
      if (typeof flagId === 'string') {
        return flagId;
      } else {
        setFlagCookie(defaultEntityId);
        return defaultEntityId;
      }
    }

    async function updateEntityId() {
      const id = await getEntityId();
      setEntityId(id);
    }

    updateEntityId();
  }, [user.flagrEntityId, user.loggedIn, userIsFetched]);

  if (status === 'success' && typeof data !== 'undefined') {
    /** if flagKey is undefined return all flags */
    if (typeof flagKey === 'undefined') {
      return {
        status,
        flags: data.response,
      };
    }
    /** find requested flag */
    const result = data.response.find((flag) => flag.flagKey === flagKey);
    return {
      status,
      variant: result?.variantKey,
      variantAttachment: result?.variantAttachment,
    };
  }
  return { status, variant: undefined };
}
